import React from 'react';

/**
 * Guards an async submit action against double-invocation (double-tap on a
 * payment confirmation button firing completeTransaction twice).
 *
 * - `isSubmitting` — wire to the trigger's `disabled` prop.
 * - `runGuarded(action)` — runs the action; concurrent calls while one is in
 *   flight are ignored. Always resets afterwards (also on failure) so the
 *   cashier can retry after a real error. Errors propagate to the caller.
 */
export const useSubmitGuard = () => {
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const inFlight = React.useRef(false);

	const runGuarded = React.useCallback(async (action: () => Promise<unknown>) => {
		if (inFlight.current) return;
		inFlight.current = true;
		setIsSubmitting(true);
		try {
			await action();
		} finally {
			inFlight.current = false;
			setIsSubmitting(false);
		}
	}, []);

	return { isSubmitting, runGuarded };
};
